/**
 * @fileoverview Input/output schemas for the extract-styleguide-v2 agent.
 *
 * This agent extracts a complete style guide from a text sample by:
 * 1. Detecting genres from the text
 * 2. Analyzing style to get slider values
 * 3. Generating a style guide with the detected genres and slider values
 */

import { z } from 'zod';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Flow Input Schema - includes text and available genre IDs.
 * Includes execution metadata (logId, model). No customPrompt — composites
 * have no prompt of their own.
 */
export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  projectId: z.string().optional().describe('Project ID for project-specific agent configuration.'),
  text: z.string().min(1).describe('The text to analyze and extract a style guide from (will be truncated to first 50k characters).'),
  availableGenreIds: z.array(z.string()).describe('List of available genre template IDs to choose from.'),
});

export type Input = z.infer<typeof InputSchema>;

/**
 * Hook Input type - domain-only fields for hooks using executeAgent.
 * The hook provides availableGenreIds from the genre templates constant.
 */
export type HookInput = {
  /** The text to analyze and extract a style guide from */
  text: string;
};

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output Schema - includes detected genres, the generated style guide, and slider values.
 */
export const OutputSchema = z.object({
  detectedGenreIds: z.array(z.string()).describe('The genre template IDs detected from the text.'),
  styleGuide: z.string().describe('The complete style guide in markdown format.'),
  meta: z.object({
    derivedTone: z.array(z.string()),
    primaryGenre: z.string(),
    contentRating: z.string(),
    pacingProfile: z.string(),
    synthesisNotes: z.string(),
  }),
  sliderValues: z.object({
    sexualContent: z.number().min(0).max(4),
    physicalViolence: z.number().min(0).max(4),
    emotionalViolence: z.number().min(0).max(4),
    profanity: z.number().min(0).max(4),
    narrativeDistance: z.number().min(0).max(4),
    visualSpecificity: z.number().min(0).max(4),
    syntacticComplexity: z.number().min(0).max(4),
    vocabularyRegister: z.number().min(0).max(4),
    descriptionDensity: z.number().min(0).max(4),
    pacingCadence: z.number().min(0).max(4),
    expositionIntegration: z.number().min(0).max(4),
    dialogueTexture: z.number().min(0).max(4),
    emotionalSaturation: z.number().min(0).max(4),
    atmosphericWeight: z.number().min(0).max(4),
  }),
  toneKeywords: z.array(z.string()).optional(),
  recommendedPhrases: z.array(z.string()).optional(),
});

export type Output = z.infer<typeof OutputSchema>;

// =============================================================================
// RE-EXPORTS FROM PRIMITIVES
// =============================================================================

// Re-export GenreDetectionOutputSchema from the primitive for backwards compatibility
export { OutputSchema as GenreDetectionOutputSchema } from '@/ai/agents/primitives/style-guides/genre-detector-v2/schema';
export type { Output as GenreDetectionOutput } from '@/ai/agents/primitives/style-guides/genre-detector-v2/schema';
